import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  CreditCard,
  ExternalLink,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { useClinicPayStatus, useStartClinicPayOnboarding, deriveClinicPayDisplayStatus } from '@/hooks/useClinicPay';
import { ApiCallError } from '@/lib/api';
import { Button, ButtonAnchor } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { IntegrationStatusBanner } from '@/components/IntegrationStatusBanner';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import { SectionCard, SaveBar } from '@/pages/settings/ClinicGeneral';
import { SettingsLoading } from './settingsChrome';
import {
  parseCardCancellationPolicy,
  type CancellationFee,
  type CardCancelAction,
  type CardCancellationPolicy,
} from '@/lib/bookingPolicies';
import type { Json } from '@/types/database.generated';

export function ClinicPayments() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const { data: payStatus, isLoading, error } = useClinicPayStatus(activeClinicId);
  const onboard = useStartClinicPayOnboarding(activeClinicId);
  const { data: clinic } = useClinic(activeClinicId);
  const updateClinic = useUpdateClinicSettings(activeClinicId);
  const [cardPolicy, setCardPolicy] = useState<CardCancellationPolicy>({
    before_deadline: { action: 'no_refund' },
    after_deadline: { action: 'no_refund' },
  });

  useEffect(() => {
    if (!clinic) return;
    setCardPolicy(
      parseCardCancellationPolicy(
        (clinic as { card_cancellation_policy?: unknown }).card_cancellation_policy,
      ),
    );
  }, [clinic]);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  async function handleStartOnboarding() {
    if (!activeClinicId) return;
    try {
      await onboard.mutateAsync(undefined);
    } catch (err) {
      console.error('pay onboarding failed', err);
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('payments.pay_onboarding_error');
      toast.error(message);
    }
  }

  if (isLoading) {
    return <SettingsLoading />;
  }
  if (error) {
    return <ErrorAlert message={t('payments.pay_status_error')} />;
  }

  const status = deriveClinicPayDisplayStatus(payStatus);
  const isPending   = !payStatus || status === 'pending';
  const isOnboarding = status === 'onboarding';
  const isActive    = status === 'active';
  const isRestricted = status === 'restricted';
  const isDisabled  = status === 'disabled';

  return (
    <div className="space-y-6">
      <IntegrationsBackLink />

      {/* Clinio Pay service availability banner (SHARED-5) */}
      <IntegrationStatusBanner integration="pay" />

      <div>
        <h2 className="text-lg font-semibold text-ds-text">
          {t('settings.integrations.cards.pay_title')}
        </h2>
        <p className="mt-1 text-sm text-ds-muted">
          {t('payments.subtitle')}
        </p>
      </div>

      {/* Test mode banner (PAY-1 ADD §7 — read-only, yellow banner) */}
      {payStatus?.test_mode && (
        <div className="flex items-center gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint px-4 py-3">
          <AlertTriangle className="h-4 w-4 shrink-0 text-ds-amber" />
          <span className="text-sm font-semibold text-ds-amber-ink">
            {t('payments.test_mode_active')}
          </span>
        </div>
      )}

      {/* Clinio Pay status card */}
      <section className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  ">
        <div className="mb-4 flex items-center gap-3">
          <CreditCard className="h-5 w-5 text-ds-teal" />
          <h3 className="text-base font-medium text-ds-text">
            {t('payments.clinio_pay_title')}
          </h3>
          {payStatus && (
            <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(status)}`}>
              {t(`payments.status_${status}`)}
            </span>
          )}
        </div>

        {/* ── State: not yet provisioned / pending ─────────────────────────── */}
        {isPending && (
          <div className="space-y-3">
            <p className="text-sm text-ds-muted">
              {t('payments.not_connected_desc')}
            </p>
            {canAdmin && (
              <Button onClick={handleStartOnboarding} disabled={onboard.isPending}>
                {onboard.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                {t('payments.activate_pay')}
              </Button>
            )}
          </div>
        )}

        {/* ── State: onboarding (Stripe KYC incomplete) ────────────────────── */}
        {isOnboarding && payStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-ds-amber-line bg-ds-amber-tint px-4 py-3">
              <AlertTriangle className="h-4 w-4 shrink-0 text-ds-amber" />
              <span className="text-sm text-ds-amber-ink">
                {t('payments.onboarding_incomplete')}
              </span>
            </div>
            {payStatus.stripe_account_id && (
              <p className="text-xs text-ds-muted">
                {t('payments.account_id')}:{' '}
                <span className="font-mono">{payStatus.stripe_account_id.slice(0, 12)}…</span>
              </p>
            )}
            {canAdmin && (
              <Button
                onClick={handleStartOnboarding}
                disabled={onboard.isPending}
                variant="secondary"
              >
                {onboard.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t('payments.resume_onboarding')}
              </Button>
            )}
          </div>
        )}

        {/* ── State: active ─────────────────────────────────────────────────── */}
        {isActive && payStatus && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-ds-green-ink">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">{t('payments.status_active')}</span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {payStatus.stripe_account_id && (
                <div>
                  <span className="text-ds-muted">{t('payments.account_id')}: </span>
                  <span className="font-mono text-ds-text">
                    {payStatus.stripe_account_id.slice(0, 12)}…
                  </span>
                </div>
              )}
              {payStatus.last_synced_at && (
                <div>
                  <span className="text-ds-muted">{t('payments.last_synced')}: </span>
                  <span className="text-ds-text">
                    {new Date(payStatus.last_synced_at).toLocaleString('da-DK')}
                  </span>
                </div>
              )}
              <div>
                <span className="text-ds-muted">{t('payments.platform_fee_percent_label')}: </span>
                <span className="text-ds-text">
                  {payStatus.platform_fee_percent} %
                </span>
              </div>
              <div>
                <span className="text-ds-muted">{t('payments.charges_enabled')}: </span>
                <span className={payStatus.charges_enabled ? 'text-ds-green-ink' : 'text-ds-muted'}>
                  {payStatus.charges_enabled ? t('common.yes') : t('common.no')}
                </span>
              </div>
            </div>

            {payStatus.stripe_account_id && (
              <ButtonAnchor
                href={`https://dashboard.stripe.com/${payStatus.stripe_account_id}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="link"
              >
                {t('payments.dashboard_link')}
                <ExternalLink />
              </ButtonAnchor>
            )}
          </div>
        )}

        {/* ── State: restricted ─────────────────────────────────────────────── */}
        {isRestricted && payStatus && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-lg border border-ds-red-line bg-ds-red-tint px-4 py-3">
              <AlertTriangle className="h-4 w-4 shrink-0 text-ds-red-ink" />
              <span className="text-sm text-ds-red-ink">
                {t('payments.stripe_restricted')}
              </span>
            </div>
            {payStatus.stripe_account_id && (
              <ButtonAnchor
                href={`https://dashboard.stripe.com/${payStatus.stripe_account_id}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="link"
              >
                {t('payments.dashboard_link')}
                <ExternalLink />
              </ButtonAnchor>
            )}
          </div>
        )}

        {/* ── State: disabled ───────────────────────────────────────────────── */}
        {isDisabled && (
          <p className="text-sm text-ds-muted">
            {t('payments.status_rejected_desc')}
          </p>
        )}
      </section>

      {/* Platform fee info (read-only — source of truth is Pay) */}
      {payStatus && isActive && (
        <section className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  ">
          <h3 className="mb-2 text-base font-medium text-ds-text">
            {t('payments.platform_fee_title')}
          </h3>
          <p className="text-sm text-ds-muted">
            {payStatus.platform_fee_percent} %
          </p>
          <p className="mt-1 text-xs text-ds-muted">
            {t('payments.platform_fee_desc')}
          </p>
        </section>
      )}

      {/* Webhook endpoint info */}
      <section className="rounded-xl border border-ds-line bg-ds-card p-6 shadow-card  ">
        <h3 className="mb-3 text-base font-medium text-ds-text">
          {t('payments.webhook_title')}
        </h3>
        <p className="mb-2 text-sm text-ds-muted">
          {t('payments.clinio_pay_webhook_desc')}
        </p>
        <div className="rounded-md bg-ds-sunk px-3 py-2 font-mono text-xs text-ds-text  ">
          https://pupgbtuzlamtircfdamp.supabase.co/functions/v1/pay-webhook-receiver
        </div>
        <p className="mt-2 text-xs text-ds-muted">
          {t('payments.clinio_pay_webhook_note')}
        </p>
      </section>

      <form
        className="space-y-4"
        onSubmit={async (e: FormEvent) => {
          e.preventDefault();
          try {
            await updateClinic.mutateAsync({
              card_cancellation_policy: cardPolicy as unknown as Json,
            });
            toast.success(t('settings.saved_toast'));
          } catch (err) {
            console.error('card cancellation policy save failed', err);
            toast.error(t('settings.save_error_toast'));
          }
        }}
      >
        <SectionCard
          title={t('booking_policies.card_policy_title')}
          subtitle={t('booking_policies.card_policy_subtitle')}
          readOnly={!canAdmin}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {(['before_deadline', 'after_deadline'] as const).map((side) => (
              <div key={side}>
                <label className="text-sm font-medium text-ds-text">
                  {t(`booking_policies.${side}`)}
                </label>
                <select
                  value={cardPolicy[side].action}
                  disabled={!canAdmin}
                  onChange={(e) =>
                    setCardPolicy((prev) => ({
                      ...prev,
                      [side]: { ...prev[side], action: e.target.value as CardCancelAction },
                    }))
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm text-ds-text"
                >
                  <option value="no_refund">{t('booking_policies.action_no_refund')}</option>
                  <option value="full_refund">{t('booking_policies.action_full_refund')}</option>
                  <option value="refund_minus_fee">{t('booking_policies.action_refund_minus_fee')}</option>
                </select>
                {cardPolicy[side].action === 'refund_minus_fee' && (
                  <FeeFields
                    fee={cardPolicy[side].fee}
                    disabled={!canAdmin}
                    onChange={(fee) =>
                      setCardPolicy((prev) => ({
                        ...prev,
                        [side]: { ...prev[side], fee },
                      }))
                    }
                  />
                )}
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-ds-muted">{t('booking_policies.card_policy_note')}</p>
        </SectionCard>
        <SaveBar canEdit={canAdmin} pending={updateClinic.isPending} />
      </form>
    </div>
  );
}

function FeeFields({
  fee,
  disabled,
  onChange,
}: {
  fee?: CancellationFee;
  disabled: boolean;
  onChange: (fee: CancellationFee) => void;
}) {
  const { t } = useTranslation();
  const type = fee?.type ?? 'none';
  return (
    <div className="mt-2 space-y-2">
      <select
        value={type}
        disabled={disabled}
        onChange={(e) =>
          onChange({
            type: e.target.value as CancellationFee['type'],
            amount_cents: fee?.amount_cents ?? 0,
            percent: fee?.percent ?? 0,
          })
        }
        className="h-10 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm   "
      >
        <option value="none">{t('booking_policies.fee_none')}</option>
        <option value="fixed">{t('booking_policies.fee_fixed')}</option>
        <option value="percent">{t('booking_policies.fee_percent')}</option>
      </select>
      {type === 'fixed' && (
        <input
          type="number"
          min={0}
          step={1}
          disabled={disabled}
          value={((fee?.amount_cents ?? 0) / 100).toString()}
          onChange={(e) =>
            onChange({
              type: 'fixed',
              amount_cents: Math.round(Number(e.target.value) * 100),
              percent: 0,
            })
          }
          className="h-10 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm   "
          aria-label={t('booking_policies.fee_amount')}
        />
      )}
      {type === 'percent' && (
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          value={fee?.percent ?? 0}
          onChange={(e) =>
            onChange({
              type: 'percent',
              amount_cents: 0,
              percent: Number(e.target.value),
            })
          }
          className="h-10 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm   "
          aria-label={t('booking_policies.fee_percent_value')}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadgeClass(status: string | null): string {
  switch (status) {
    case 'active':
      return 'bg-ds-green-tint text-ds-green-ink';
    case 'onboarding':
      return 'bg-ds-amber-tint text-ds-amber-ink';
    case 'restricted':
      return 'bg-ds-red-tint text-ds-red-ink';
    case 'disabled':
      return 'bg-ds-neutral-tint text-ds-text';
    default:
      return 'bg-ds-neutral-tint text-ds-text';
  }
}
